import React, { useEffect, useState, useRef, useLayoutEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import Select from 'react-select';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Tab } from '@headlessui/react';

// HOOKS
import { useRefreshToken } from '../hooks/useTokenManager';
import { auth } from '../config/fb';
// COMPONENTS
import Total from '../components/Total';
import LeftComboGraph from '../components/LeftComboGraph';
import RightComboGraph from '../components/RightComboGraph';
import DateSelection from '../components/DateSelection';
import Table1 from '../components/Table1';
import Table2 from '../components/Table2b';
import Table3 from '../components/Table3';
import NegNameTraineeScoreTable from '../components/NegNameTraineeScoreTable';
import TrainingProgTable from '../components/TrainingProgTable';
import CertificateCard from '../components/CertificateCard';

// API
// import { fetchChapterDataApi } from '../apis/data.api';
import { getUnityModuleCount, deleteSession } from '../apis/user.api';
import { verifyUser, fetchOrganizationsApi, fetchAdminVisibilityApi } from '../apis/admin.api';
import { HIDDEN_FROM_ADMIN_TYPES } from '../utils/orgTypes';
// PROC
import { procAdminUnityData } from '../processes/fetchUserDataRtkQ';
// REDUX
import {
  setCurrentPage,
  setUnityAppRunning,
  setSentTokenMessage
} from '../redux/app.slice';
import { useGetChaptersQuery, useGetUserDataQuery, useGetUserDataMultiRegionQuery } from '../apis/rtkData.api';
// import { getChapterGroupApi } from '../apis/data.api';
import { 
  resetUser,              // eslint-disable-line
  setLoginStatus
} from "../redux/user.slice"; // eslint-disable-line

// Import icons
import SessionIcon from '../assets/session-32x32.png';
import NegotiationIcon from '../assets/negotiation-32x32.png';

const AdminDash = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userData, setUserData] = useState({});
  const [userLoggedIn, setUserLoggedIn] = useState(false); // eslint-disable-line
  const [organizationArray, setOrganizationArray] = useState([]);
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  // Every org the admin may pick (internal/test removed), each flagged `expired`.
  const [pickableOrgs, setPickableOrgs] = useState([]);
  // Organisations whose subscription is over are left out of the picker unless this is on.
  const [includeExpired, setIncludeExpired] = useState(false);
  const [startDate, setStartDate] = useState(new Date('09/01/23'));
  const [endDate, setEndDate] = useState(new Date());
  const [table1Data, setTable1Data] = useState([]);
  const [table3Data, setTable3Data] = useState([]);
  // const [trainingProgData, setTrainingProgData] = useState([]);
  const [negTableData, setNegTableData] = useState([]);
  const [topNegTableData, setTopNegTableData] = useState([]);
  const [duration, setDuration] = useState(0);
  const [maxModuleCount, setMaxModuleCount] = useState(0);
  // const [chapters, setChapters] = useState([]);
  // const [ chapterGroup, setChapterGroup ] = useState({});
  const [moduleCount, setModuleCount] = useState(0);
  const [negotiationCount, setNegotiationCount] = useState(0);
  const [traineeNames, setTraineeNames] = useState([]);
  const [negotiationTime, setNegotiationTime] = useState(0);
  const [negotiationTitles, setNegotiationTitles] = useState([]); // eslint-disable-line
  const [negotiationLengths, setNegotiationLengths] = useState([]); // eslint-disable-line
  const { loginStatus } = useSelector((state) => state.user, shallowEqual); // eslint-disable-line
  const { reactVerbosity } = useSelector((state) => state.app); // eslint-disable-line
  const { privacy } = useSelector((state) => state.app, shallowEqual); // eslint-disable-line
  const { user } = useSelector((state) => state.user, shallowEqual); // eslint-disable-line
  const { idToken, expiresAt, isRefreshing } = useSelector((state) => state.token);
  const [isHandlingTokenError, setIsHandlingTokenError] = useState(false);
  useRefreshToken();

  const [activeTab, setActiveTab] = useState('Organizations');
  const [isTabsSticky, setIsTabsSticky] = useState(false);
  const tabsRef = useRef(null);

  const [selectedDept, setSelectedDept] = useState({ value: 'all', label: 'All' });
  const [selectedTeam, setSelectedTeam] = useState({ value: 'all', label: 'All' });
  const [selectedRegion, setSelectedRegion] = useState({ value: 'all', label: 'All' });
  const [departmentOptions, setDepartmentOptions] = useState([{ value: 'all', label: 'All' }]);
  const [teamOptions, setTeamOptions] = useState([{ value: 'all', label: 'All' }]);
  const [regionOptions, setRegionOptions] = useState([{ value: 'all', label: 'All' }]);
  const [showOrganizationSelector, setShowOrganizationSelector] = useState(true);
  const [showDepartmentSelector, setShowDepartmentSelector] = useState(false);
  const [showTeamSelector, setShowTeamSelector] = useState(false);
  const [showRegionSelector, setShowRegionSelector] = useState(false);
  const [showResetSelectors, setShowResetSelectors] = useState(false);
  const [showCertificateCard, setShowCertificateCard] = useState(true);

  const selection = {
    startDate,
    endDate
  };

  // const {
  //   data: chData,
  //   error,
  //   isLoading: isLoadingChData,
  //   isSuccess,
  //   isError,
  //   isFetching
  // } = useGetChaptersQuery({
  //   authToken: idToken,
  //   user,
  //   selection
  // });

  // Org admins see usage aggregated across ALL regions by default. The dashboard always
  // fetches via the multi-region endpoint, which fans out to every region and merges the
  // results. The original single-region useGetUserDataQuery is kept in rtkData.api as a
  // fallback but is no longer the dashboard's primary source.
  const {
    data: usrData,
    error: usrErr,
    isLoading: isLoadingUsrData,
    refetch: refetchUserData
  } = useGetUserDataMultiRegionQuery({
    authToken: idToken,
    user,
    selection,
    orgName: selectedOrg?.label
  });

  const memoizedFetchUserDataProcess = useCallback(async () => {
    if (usrData == null) return;
    let startTime = 0;
    reactVerbosity === 4 && (startTime = performance.now());
    const maxModuleCount = await getUnityModuleCount();
    // console.log('maxModuleCount:', maxModuleCount)
    setMaxModuleCount(maxModuleCount.data);

    let filteredInputData = usrData;
    if (selectedOrg != null && usrData != null) {
      // console.log('memoizedFetchUserDataProcess usrData:', usrData, selectedOrg, moduleCount, reactVerbosity )
      console.log('filteredInputData:', filteredInputData)
      filteredInputData = usrData.filter(d =>
        d.organizationId === selectedOrg.label || d.organization?.name === selectedOrg.label
      );
    }

    const { userDataObj } = await procAdminUnityData(filteredInputData, moduleCount);

    // console.log('userDataObj:', userDataObj)
    setDuration(userDataObj.duration);
    setNegotiationCount(userDataObj.negotiations);
    setTraineeNames(userDataObj.names);
    setNegotiationTime(userDataObj.time);
    setNegotiationTitles(userDataObj.titles);
    setNegotiationLengths(userDataObj.lengths);
    setModuleCount(userDataObj.modules);
    setUserData(userDataObj);

    reactVerbosity === 4 && console.log(
      `AdminDash fetchUserDataProcess took ${performance.now() - startTime} milliseconds`
    );
  }, [usrData, selectedOrg, reactVerbosity]);
  // }, [usrData.length, selectedOrg, moduleCount, reactVerbosity]);

  useEffect(() => {
    dispatch(setCurrentPage('adminDash'));
    fetchOrganizations();
    refetchUserData();
  }, []);

  // useEffect(() => {
  //   reactVerbosity === 4 && console.log('CHCHCH=========> rtkq chData:', reactVerbosity, chData);
  //   setChapters(chData);
  // }, [chData, reactVerbosity]);

  // useEffect(() => {
  //   getChapterGroup();
  // }, [user]);

  useEffect(() => {
    if (Object.keys(userData).length > 0) {
      buildDataForTable1();
      buildDataForTable3();
      buildDataForNegTable();
      buildDataForTopNegTable();
      // buildDataForTrainingProgressTable();
    }
  }, [userData.sessionCount]);

  useEffect(() => {
    const handleTokenError = async () => {
      if (isHandlingTokenError) return;
      setIsHandlingTokenError(true);

      try {
        // Force Firebase to mint a fresh ID token, then retry. NOTE: useRefreshToken() is
        // a React hook (already mounted at the top of this component) and must NOT be
        // called here inside a callback — doing so violates the rules of hooks.
        if (auth.currentUser) {
          await auth.currentUser.getIdToken(true);
        }
        await refetchUserData();
        setIsHandlingTokenError(false);
      } catch (refreshError) {
        console.error('**** AdminDash: Token refresh failed:', refreshError);
        dispatch(setCurrentPage('login'));
        navigate('/login', { replace: true });
      }
    };

    if (!isLoadingUsrData && usrErr && usrErr.message && usrErr.message.includes("token")) {
      handleTokenError();
    } else if (!isLoadingUsrData) {
      memoizedFetchUserDataProcess();
    }
  }, [isLoadingUsrData, usrErr, memoizedFetchUserDataProcess, selectedOrg, startDate, endDate]);

  useEffect(() => {
    const handleScroll = () => {
      if (tabsRef.current) {
        const tabsTop = tabsRef.current.getBoundingClientRect().top;
        setIsTabsSticky(tabsTop <= 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const [showFloatingButton, setShowFloatingButton] = useState(false);

  useLayoutEffect(() => {
    // console.log('AdminDash component mounted');
    
    // Log the heights of various elements
    // console.log('Body scroll height:', document.body.scrollHeight);
    // console.log('Document element scroll height:', document.documentElement.scrollHeight);
    // console.log('Window inner height:', window.innerHeight);

    // Try to find the scrollable container
    const findScrollableParent = (element) => {
      if (!element) return null;
      if (element.scrollHeight > element.clientHeight) {
        // console.log('Potential scrollable element found:', element);
        return element;
      }
      return findScrollableParent(element.parentElement);
    };

    const adminDashElement = document.querySelector('.bg-gray-100'); // Adjust this selector if needed
    let scrollableParent = null;

    if (adminDashElement) {
      scrollableParent = findScrollableParent(adminDashElement);
      // console.log('Scrollable parent:', scrollableParent);
    } else {
      // console.log('AdminDash element not found');
    }

    // Attempt to log scroll events on both window and potential scrollable parent
    const handleScroll = (event) => {
      // console.log('Scroll event detected on:', event.target);
      // console.log('Scroll position:', event.target.scrollTop);
      
      // Update floating button visibility
      setShowFloatingButton(event.target.scrollTop > 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    if (scrollableParent) {
      scrollableParent.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollableParent) {
        scrollableParent.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const contentRef = useRef(null);

  useLayoutEffect(() => {
    if (contentRef.current) {
      // console.log('Rendered content height:', contentRef.current.offsetHeight);
    }
  }, []);

  const fetchOrganizations = async () => {
    try {
      const orgRes = await fetchOrganizationsApi(idToken);
      // console.log('---> orgRes:', orgRes)

      /* Internal and test orgs are never offered to the platform admin. Which
         orgs are expired comes back in the same call whatever the switch says,
         so the switch only filters this list — no refetch. If the visibility
         call fails the type rule still applies; expired orgs then stay listed
         (untagged) rather than vanishing for an unknown reason. */
      let expiredNames = new Set();
      try {
        const vis = await fetchAdminVisibilityApi(idToken);
        expiredNames = new Set((vis.data?.data?.expiredOrgNames) || []);
      } catch (visErr) {
        console.log('----x fetchAdminVisibility:', visErr);
      }
      setPickableOrgs(
        orgRes.data
          .filter((o) => !HIDDEN_FROM_ADMIN_TYPES.includes(o.orgType))
          .map((o) => ({ ...o, expired: expiredNames.has(o.name) }))
      );
    } catch (err) {
      console.log('----x fetchOrganizations:', err);
    }
  };

  // The picker: every pickable org, minus expired ones unless the switch is on.
  useEffect(() => {
    const visible = pickableOrgs.filter((o) => includeExpired || !o.expired);
    const orgResOptions = visible.map((o, i) => ({
      // `label` stays the bare name: it is what the data query sends as orgName.
      key: i, value: o._id, label: o.name, expired: o.expired
    }));
    setOrganizationArray(visible);
    setOrganizationOptions(orgResOptions);
    setSelectedOrg((prev) => orgResOptions.find((o) => prev && o.label === prev.label) || orgResOptions[0]);
  }, [pickableOrgs, includeExpired]);

  // const getChapterGroup = async () => {
  //   const chGrp = await getChapterGroupApi(idToken, 'default');
  //   setChapterGroup(chGrp);
  // }

  const handleDateChange = useCallback((start, end) => {
    console.log('handleDateChange start, end:', start, end);
    setStartDate(start);
    setEndDate(end);
    // Don't call fetchUserDataProcess here, it will be triggered by the useEffect
  }, []);

  // const buildDataForTrainingProgressTable = async () => {
  //   // userData.sessions ==> sessions with length, modules and a user
  //   const lessons = {};
  //   const quizzes = {};
  //   const selfAssessments = {};

  //   const tableData =  // eslint-disable-line
  //     userData.sessions &&
  //     userData.sessions.map((s, index) => {
  //     // console.log("==========[> index, s:", index, s)
  //       return s.unitysessionmodules.map((m, i) => { // eslint-disable-line
  //         // console.log('==========[> i, m:', i,  m)
  //         if (m.moduleType === 'lesson' && m.userScore !== -1 && m.userScore !== 0) {
  //           let date = new Date(s.sessionStartTime).toISOString().split('T')[0];
  //           const dateArr = date.split('-');
  //           date = dateArr[1] + '-' + dateArr[2] + '-' + dateArr[0];
  //           if (!lessons[s.user.name]) lessons[s.user.name] = {};
  //           if (!lessons[s.user.name][m.moduleName]) { lessons[s.user.name][m.moduleName] = {}; }
  //           lessons[s.user.name][m.moduleName] = {
  //             moduleType:'Lesson',
  //             module: m.moduleName,
  //             user: s.user.name,
  //             userScore: m.userScore,
  //             complete: m.userScore === -1 ? 'X' : '\u2713',
  //             date
  //           };
  //         }

  //         if (m.moduleType === 'quiz' && m.userScore !== -1 && m.userScore !== 0) {
  //           let date = new Date(s.sessionStartTime).toISOString().split('T')[0];
  //           const dateArr = date.split('-');
  //           date = dateArr[1] + '-' + dateArr[2] + '-' + dateArr[0];

  //           if (!quizzes[s.user.name]) quizzes[s.user.name] = {};
  //           if (!quizzes[s.user.name][m.moduleName]) { quizzes[s.user.name][m.moduleName] = {}; }
  //           quizzes[s.user.name][m.moduleName] = {
  //             moduleType: 'Quiz',
  //             module: m.moduleName,
  //             user: s.user.name,
  //             userScore: m.userScore,
  //             complete: m.userScore === -1 ? 'X' : '\u2713',
  //             date
  //           };
  //         }
          
  //         if (m.moduleType === 'selfassessment' && m.userScore !== -1 && m.userScore !== 0) {
  //           let date = new Date(s.sessionStartTime).toISOString().split('T')[0];
  //           const dateArr = date.split('-');
  //           date = dateArr[1] + '-' + dateArr[2] + '-' + dateArr[0];
  //           if (!selfAssessments[s.user.name]) selfAssessments[s.user.name] = {};
  //           if (!selfAssessments[s.user.name][m.moduleName]) { selfAssessments[s.user.name][m.moduleName] = {}; }
  //           selfAssessments[s.user.name][m.moduleName] = {
  //             moduleType: 'Self Assessment',
  //             module: m.moduleName,
  //             user: s.user.name,
  //             userScore: m.userScore,
  //             complete: m.userScore === -1 ? 'X' : '\u2713',
  //             date
  //           };
  //           // console.log('--> buildDataForTrainingProgressTable selfAssessments:', selfAssessments)
  //         }

  //         if (Object.keys(lessons).length === 0) {
  //           lessons[s.user.name] = {};
  //         }
  //         if (Object.keys(quizzes).length === 0) {
  //           quizzes[s.user.name] = {};
  //         }
  //         if (Object.keys(selfAssessments).length === 0) {
  //           selfAssessments[s.user.name] = {};
  //         }
          
  //         if (m.moduleType !== 'negotiation') {
  //           let date = new Date(s.sessionStartTime).toISOString().split('T')[0];
  //           const dateArr = date.split('-');
  //           date = dateArr[1] + '-' + dateArr[2] + '-' + dateArr[0];
  //           // console.log("===> m:", m)
  //           return {
  //             moduleType: m.moduleType,
  //             module: m.moduleName,
  //             user: s.user.name,
  //             userScore: m.userScore,
  //             complete: m.userScore === -1 ? 'X' : '\u2713',
  //             date
  //           };
  //         }
  //       });
  //     });

  //   // console.log('==========> chapters:', chapters)
  //   let chapterLessons = [];
  //   let chapterQuizzes = [];
  //   let chapterSelfAssessments = [];
  //   chapters && chapters.length > 0 && chapters.forEach((c) => {
  //       c.lessons.forEach((l) => {
  //         // console.log('title, moduleName:', l.title, l.moduleName)
  //         chapterLessons = [
  //           ...chapterLessons,
  //           {
  //             chapter: c.title,
  //             chapterId: c._id,
  //             chapterIndex: c.chapterIndex,
  //             number: l.moduleIndex,
  //             moduleType: 'Lesson',
  //             module: l.moduleName,
  //             userScore: -1,
  //             complete: 'X',
  //             date: ''
  //           }
  //         ];
  //       });
  //       c.quizzes.forEach((q) => {
  //         // console.log('====> q:', q, chapterQuizzes)
  //         chapterQuizzes = [
  //           ...chapterQuizzes,
  //           {
  //             chapter: c.title,
  //             chapterIndex: c.chapterIndex,
  //             chapterId: c._id,
  //             number: q.moduleIndex,
  //             moduleType: 'Quiz',
  //             module: q.moduleName,
  //             userScore: -1,
  //             complete: 'X',
  //             date: ''
  //           }
  //         ];
  //       });

  //       c.selfassessments && c.selfassessments.forEach((sa) => {
  //         // console.log('====> sa:', sa, chapterSelfAssessments)
  //         chapterSelfAssessments = [
  //           ...chapterSelfAssessments,
  //           {
  //             chapter: c.title,
  //             chapterIndex: c.chapterIndex,
  //             chapterId: c._id,
  //             number: parseInt(sa.moduleIndex),
  //             moduleType: 'Self Assessment',
  //             module: sa.moduleName,
  //             userScore: -1,
  //             complete: 'X',
  //             date: ''
  //           }
  //         ];
  //       });

  //   });

  //   const rebuiltTableData = [];
  //   const lessonKeys = Object.keys(lessons);
  //   lessonKeys.forEach((k) => {
  //     chapterLessons.forEach((l) => {
  //       l.user = k;
  //       if (!lessons[k][l.module]) {
  //         lessons[k][l.module] = { ...l };
  //       } else {
  //         lessons[k][l.module].chapter = l.chapter;
  //         lessons[k][l.module].number = l.moduleIndex;
  //       }
  //       rebuiltTableData.push(lessons[k][l.module]);
  //     });
  //   });
  //   const quizKeys = Object.keys(quizzes);
  //   quizKeys.forEach((k) => {
  //     chapterQuizzes.forEach((q) => {
  //       q.user = k;
  //       if (!quizzes[k][q.module]) {
  //         quizzes[k][q.module] = { ...q };
  //       } else {
  //         quizzes[k][q.module].chapter = q.chapter;
  //         quizzes[k][q.module].number = q.moduleIndex;
  //       }
  //       rebuiltTableData.push(quizzes[k][q.module]);
  //     });
  //   });
    
  //   const selfAssessmentKeys = Object.keys(selfAssessments);
  //   selfAssessmentKeys.forEach((k) => {
  //     chapterSelfAssessments.forEach((sa) => {
  //       sa.user = k;
  //       // console.log("====> sa.user, sa (438):", sa.user, sa)
  //       // console.log("======> selfAssessments[k] (439):", selfAssessments[k])
  //       if (!selfAssessments[k][sa.module]) {
  //         selfAssessments[k][sa.module] = { ...sa };
  //       } else {
  //         selfAssessments[k][sa.module].chapter = sa.chapter;
  //         selfAssessments[k][sa.module].number = sa.moduleIndex;
  //       }
  //       // console.log('!!!!!! selfAssessments[k][sa.module] (500):', selfAssessments[k][sa.module])
  //       rebuiltTableData.push(selfAssessments[k][sa.module]);
  //     });
  //   });

  //   // console.log('=======> lessons:', lessons)
  //   // console.log('===> chapterLessons:', chapterLessons)
  //   // console.log('=======> quizzes:', quizzes)
  //   // console.log('=======> selfAssessments:', selfAssessments)
  //   // console.log('===> chapterQuizzes:', chapterQuizzes)
  //   // console.log('===> rebuiltTableData:', rebuiltTableData)

  //   let chapterGroupObj = {};
  //   rebuiltTableData.forEach((m) => {
  //     // if (m.chapterId) {
  //       if (!chapterGroupObj[m.chapterIndex]) chapterGroupObj[m.chapterIndex] = [];
  //       chapterGroupObj[m.chapterIndex] = [...chapterGroupObj[m.chapterIndex], m];
  //     // }
  //   })
  //   const cgKeys = Object.keys(chapterGroupObj);
  //   let concatenatedTableData = [];
  //   cgKeys.map((k) => {
  //     return concatenatedTableData = [...concatenatedTableData, chapterGroupObj[k]]
  //   })

  //   // console.log("===> concatenatedTableData.flat():", concatenatedTableData.flat())
  //   setTrainingProgData(concatenatedTableData.flat());
  //   return concatenatedTableData.flat();

  // };

  const buildDataForTable1 = async () => {
    // console.log('+++++> USER DATA:', userData)
    const names = Object.keys(userData.moduleCountPerName);
    let maxSessions = 0;
    names.forEach((n) => {
      // console.log('userData.sessionCountObj[n]:', n, userData.sessionCountObj[n], parseInt(userData.sessionCountObj[n]))
      maxSessions = maxSessions + parseInt(userData.sessionCountObj[n]);
      // console.log('maxSessions:', maxSessions)
    })

    const tableData = names.map((n, id) => {
      let negotiations = 0;
      if (userData.negotiationsPerUser[n]) {
        negotiations = userData.negotiationsPerUser[n].length;
      }
      // console.log('Processing table1Data for:', n)
      return {
        name: n,
        region: (userData.regionPerName?.[n] || []).join(', '),
        modules: `${userData.moduleCountPerName[n]}/${maxModuleCount}`,
        negotiations,
        negotiationTime: Math.round(userData.lengthsObj[n]) || 0,
        sessionCount: `${userData.sessionCountObj[n]}/${maxSessions}`,
      }
    })
    // console.log('======> Table1 tableData:', tableData)
    await setTable1Data(tableData);
    return tableData;
  };

  const buildDataForTable3 = async () => {
    // console.log('buildDataForTable3 +++++> USER DATA:', userData)
    const names = Object.keys(userData.moduleCountPerName);
    let maxSessions = 0;
    names.forEach((n) => {
      // console.log('userData.sessionCountObj[n]:', n, userData.sessionCountObj[n], parseInt(userData.sessionCountObj[n]))
      maxSessions = maxSessions + parseInt(userData.sessionCountObj[n]);
      // console.log('maxSessions:', maxSessions)
    })

    const tableData = names.map((n, id) => {
      let negotiations = 0;
      if (userData.negotiationsPerUser[n]) {
        negotiations = userData.negotiationsPerUser[n].length;
      }
      // console.log('Processing table3Data for:', n)
      // console.log('userData.moduleCountPerName:', userData.moduleCountPerName)
      // console.log('userData.moduleCountPerName[n]:', userData.moduleCountPerName[n])
      return {
        name: n,
        region: (userData.regionPerName?.[n] || []).join(', '),
        // organization: s.organization.name,
        modules: `${userData.moduleCountPerName[n]}/${maxModuleCount}`,
        // modules: `${userData.uniqueModuleNamesPerUser[n].length}/${maxModuleCount}`,
        sessions: userData.sessionsPerName[n].length,
        negotiations,
        negotiationTime: Math.round(userData.lengthsObj[n]) || 0,
        sessionCount: `${userData.sessionCountObj[n]}/${maxSessions}`,
        // sessionCountOrg: userData.sessionCountPerOrgObj[s.organization.name]
      }
    })
    // console.log('======> tableData3:', tableData)
    await setTable3Data(tableData);
    return tableData;
  };


  const buildDataForNegTable = async () => {
    // console.log('+++++> USER DATA:', userData)
    const tableData =
      userData.sessions &&
      userData.sessions.map((s, index) => {
        // console.log("==========[> index, s:", index, s)
        return s.unitysessionmodules.map((m, i) => { // eslint-disable-line
          // console.log('=====> buildDataForNegTable: m:', m, m.userScore)
          if (
            m.moduleType === 'negotiation' &&
            m.userScore > 0 &&
            m.endTime - m.startTime > 0
          ) {
            // console.log('==========[> i, m:', i,  m)
            // console.log('xxxxxxxx >> m.userScore:', m.userScore)
            // console.log('xxxxxxxx >> m.maxScore:', m.maxScore)
            let date = new Date(s.sessionStartTime).toISOString().split('T')[0];
            const dateArr = date.split('-');
            date = dateArr[1] + '-' + dateArr[2] + '-' + dateArr[0];
            let returnObj = {
              negotiation: m.moduleName,
              user: s.user.name,
              region: (s._region || '').toUpperCase(),
              userScore: m.userScore,
              maxScore: m.maxScore,
              date
            }
            // console.log('!!!!!! returnObj:', returnObj)
            return returnObj;
          }
        }
        );
      });
    // console.log('(573) ===> buildDataForNegTable tableData DATA:', tableData)
    const arrFiltered = await filterNull(tableData);
    const flattened = arrFiltered.flat();
    const flattenedFiltered = flattened.filter((e) => !Array.isArray(e));
    setNegTableData(flattenedFiltered);
    // console.log('=============> (578) buildDataForNegTable flattenedFiltered DATA:', flattenedFiltered)
    return flattenedFiltered;
  };

  const buildDataForTopNegTable = async () => {
    // console.log(' buildDataForTopNegTable +++++> USER DATA:', userData)
    // TOP NEGOTIATIONS
    const validModuleTypes = Object.keys(userData.totalModulesByType);
    const tableData =
      userData.sessions &&
      userData.sessions.map((s, index) => {
        return s.unitysessionmodules.map((m, i) => {
          if (
            m.moduleType === 'negotiation' &&
            m.userScore > 0 &&
            s.sessionLength > 0 &&
            validModuleTypes.includes(m.moduleName)
          ) {
            // console.log('==========[> i, m:', i,  m, m.moduleName, userData.totalModulesByType)
            // console.log("=========> moduleScores:", moduleScores)
            let date = new Date(s.sessionStartTime).toISOString().split('T')[0];
            const dateArr = date.split('-');
            date = dateArr[1] + '-' + dateArr[2] + '-' + dateArr[0];
            const moduleCountByType = userData.totalModulesByType[m.moduleName]
              ? userData.totalModulesByType[m.moduleName].length
              : 0;
            // console.log('ADMIN TopNegTable moduleCountByType:', moduleCountByType)
            const maxUserScore = Math.max(
              ...userData.moduleScoreObj[m.moduleName]
            );
            return {
              negotiation: m.moduleName,
              user: s.user.name,
              userScore: maxUserScore,
              maxScore: m.maxScore,
              moduleCountByType,
              date
            };
          } else return null;
        });
      });
    const arrFiltered = await filterNull(tableData);
    const flattened = arrFiltered.flat();
    const flattenedFiltered = flattened.filter((e) => !Array.isArray(e));
    const uniqueModules = Array.from(
      new Set(flattenedFiltered.map((a) => a.negotiation))
    ).map((negotiation) => {
      return flattenedFiltered.find((a) => a.negotiation === negotiation);
    });
    setTopNegTableData(uniqueModules);
    // console.log('buildDataForTopNegTable uniqueModules DATA:', uniqueModules)
    return uniqueModules;
  };

  const filterNull = (tableData) => {
    return tableData.map((e) => {
      return e.map((m) => {
        if (m !== null && m !== undefined) {
          return m;
        }
        return [];
      });
    });
  };

  const setDefaultDates = async (start, end) => {
    await setStartDate(start);
    await setEndDate(end);
  };
  const handleStartDate = (date) => {
    setStartDate(date);
  };
  const handleEndDate = (date) => {
    setEndDate(date);
  };

  const handleSelectUserOrg = (selection) => {
    setSelectedOrg(selection);
    const selectedOrgObj = organizationArray.find((o) => o.name === selection.label);
  }

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      color: 'black',
      width: '220px',
      borderRadius: '0.5rem', // rounded-lg
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', // shadow-md
      '&:hover': {
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', // hover:shadow-lg
      },
      transition: 'box-shadow 200ms ease-in-out',
      "@media only screen and (max-width: 850px)": {
        ...provided["@media only screen and (max-width: 1200px)"],
        width: "150px",
      },
      "@media only screen and (max-width: 790px)": {
        ...provided["@media only screen and (max-width: 1200px)"],
        width: "110px",
      },
      "@media only screen and (max-width: 650px)": {
        ...provided["@media only screen and (max-width: 1200px)"],
        width: "90px",
      },
    }),
    option: provided => ({
      ...provided,
      color: 'black'
    }),
    singleValue: provided => ({
      ...provided,
      color: 'black'
    })
  }

  const handleFloatingButtonClick = () => {
    console.log("Arrow up button clicked!");
    
    // Find the scrollable parent (if it exists)
    const adminDashElement = document.querySelector('.bg-gray-100');
    const scrollableParent = adminDashElement ? findScrollableParent(adminDashElement) : null;

    // Scroll to top
    if (scrollableParent && scrollableParent !== document.documentElement && scrollableParent !== document.body) {
      // If there's a specific scrollable parent, scroll it to top
      scrollableParent.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Otherwise, scroll the window to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Helper function to find scrollable parent (you can place this outside the component if you prefer)
  const findScrollableParent = (element) => {
    if (!element) return null;
    if (element.scrollHeight > element.clientHeight) {
      return element;
    }
    return findScrollableParent(element.parentElement);
  };

  
  const handleSelectDepartment = (option) => {
    setSelectedDept(option);
    // Fetch and update team options based on selected department
  };

  const handleSelectTeam = (option) => {
    setSelectedTeam(option);
    // Update any necessary state or fetch data based on selected team
  };

  const handleSelectRegion = (option) => {
    setSelectedRegion(option);
    // Update any necessary state or fetch data based on selected region
  };

  const handleReset = () => {
    setSelectedOrg({ value: organizationOptions[0].value, label: organizationOptions[0].label });
    setSelectedDept({ value: 'all', label: 'All' });
    setSelectedTeam({ value: 'all', label: 'All' });
    setSelectedRegion({ value: 'all', label: 'All' });
    // Fetch data with reset filters
  };

  return (
    <div className="bg-gray-100 min-h-screen p-6 relative" ref={contentRef}>
      {/* Floating Arrow Up Button */}
      {showFloatingButton && (
        <button
          onClick={handleFloatingButtonClick}
          className="fixed bottom-8 left-8 bg-blue-500 hover:bg-blue-600 bg-opacity-70 hover:bg-opacity-100 text-white p-3 rounded-full shadow-lg transition duration-300 ease-in-out z-50 flex items-center justify-center"
          aria-label="Scroll to top"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className="h-6 w-6" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M5 10l7-7m0 0l7 7m-7-7v18" 
            />
          </svg>
        </button>
      )}

      <div className="space-y-6">
        <header>
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Insights</h1>
          <div className="flex flex-wrap items-end space-x-4 mb-4">
            <div className="flex-1 min-w-[200px] max-w-[250px]">
              <label htmlFor="date-selection" className="mb-1 text-sm font-medium text-gray-700">Date Range</label>
              <DateSelection
                id="date-selection"
                start={startDate}
                end={endDate}
                setDefaultDates={setDefaultDates}
                handleStartDate={handleStartDate}
                handleEndDate={handleEndDate}
                handleDateChange={handleDateChange}
              />
            </div>
            {showOrganizationSelector && (
              <div className="flex-1 min-w-[200px] max-w-[250px]">
                <label htmlFor="org-select" className="mb-1 text-sm font-medium text-gray-700">Organization</label>
                <Select
                  id="org-select"
                  value={selectedOrg}
                  options={organizationOptions}
                  onChange={(option) => handleSelectUserOrg(option)}
                  formatOptionLabel={(o) => (
                    <span>
                      {o.label}
                      {o.expired && (
                        <span className="ml-2 rounded bg-gray-200 px-1 text-[10px] font-semibold uppercase text-gray-600">Expired</span>
                      )}
                    </span>
                  )}
                  styles={customStyles}
                />
                <label className="mt-1 flex items-center space-x-2 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    checked={includeExpired}
                    onChange={(e) => setIncludeExpired(e.target.checked)}
                    className="h-3 w-3"
                  />
                  <span>Include expired subscriptions</span>
                </label>
              </div>
            )}
            {showDepartmentSelector && (
              <div className="flex-1 min-w-[200px] max-w-[250px]">
                <label htmlFor="dept-select" className="mb-1 text-sm font-medium text-gray-700">Department</label>
                <Select
                  id="dept-select"
                  value={selectedDept}
                  options={departmentOptions}
                  onChange={(option) => handleSelectDepartment(option)}
                  styles={customStyles}
                />
              </div>
            )}
            {showTeamSelector && (
              <div className="flex-1 min-w-[200px] max-w-[250px]">
                <label htmlFor="team-select" className="mb-1 text-sm font-medium text-gray-700">Team</label>
                <Select
                  id="team-select"
                  value={selectedTeam}
                  options={teamOptions}
                  onChange={(option) => handleSelectTeam(option)}
                  styles={customStyles}
                />
              </div>
            )}
            {showRegionSelector && (
              <div className="flex-1 min-w-[200px] max-w-[250px]">
                <label htmlFor="region-select" className="mb-1 text-sm font-medium text-gray-700">Region</label>
                <Select
                  id="region-select"
                  value={selectedRegion}
                  options={regionOptions}
                  onChange={(option) => handleSelectRegion(option)}
                  styles={customStyles}
                />
              </div>
            )}
            {showResetSelectors && (<button
              onClick={handleReset}
              className="px-4 py-2 text-blue-600 hover:text-blue-800 transition-colors duration-200 underline self-end"
            >
              Reset
            </button>)}
          </div>
        </header>

        <div className="flex flex-col md:flex-row gap-6 h-[155px]">
          <div className="w-full md:w-[74%] flex flex-col md:flex-row gap-6">
            <div className="w-full md:w-1/2 bg-white rounded-lg shadow p-6">
              <div className="flex items-center mb-4">
                <img src={SessionIcon} alt="Session Icon" className="w-8 h-8 mr-3" />
                <h2 className="text-xl font-semibold">Session</h2>
              </div>
              <div className="flex justify-between">
                <div className="w-1/3">
                  <p className="text-sm text-gray-500">Amount</p>
                  <p className="text-3xl font-bold text-[#1F2937]">{userData.sessionCount || 0}</p>
                </div>
                <div className="w-1/3">
                  <p className="text-sm text-gray-500">Time (min)</p>
                  <p className="text-3xl font-bold text-[#1F2937]">{duration || 0}</p>
                </div>
                <div className="w-1/10"></div>
              </div>
            </div>
            <div className="w-full md:w-1/2 bg-white rounded-lg shadow p-6">
              <div className="flex items-center mb-4">
                <img src={NegotiationIcon} alt="Negotiation Icon" className="w-8 h-8 mr-3" />
                <h2 className="text-xl font-semibold">Negotiation</h2>
              </div>
              <div className="flex justify-between">
                <div className="w-1/3">
                  <p className="text-sm text-gray-500">Amount</p>
                  <p className="text-3xl font-bold text-[#1F2937]">{negotiationCount || 0}</p>
                </div>
                <div className="w-1/3">
                  <p className="text-sm text-gray-500">Time (min)</p>
                  <p className="text-3xl font-bold text-[#1F2937]">{((negotiationTime / 1000 / 60) || 0).toFixed(0)}</p>
                </div>
                <div className="w-1/10"></div>
              </div>
            </div>
          </div>
          {showCertificateCard && (<div className="w-full md:w-[26%]">
            <CertificateCard email={user.email} startDate={startDate} endDate={endDate} />
          </div>)}
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          {userData.sessionsPerDate && userData.sessionTimePerDate && (
            <LeftComboGraph
              sessionsPerDate={[userData.sessionsPerDate]}
              sessionTimePerDate={userData.sessionTimePerDate}
              text={['Sessions vs. Date', 'Session Time vs. Date']}
              reactVerbosity={reactVerbosity}
            />
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          {userData.negotiationTimePerDate && (
            <RightComboGraph
              negotiationTimePerDate={userData.negotiationTimePerDate}
              negotiationCountPerDate={userData.negotiationCountPerDate}
              reactVerbosity={reactVerbosity}
            />
          )}
        </div>

        <div 
          ref={tabsRef}
          className={`${isTabsSticky ? 'sticky top-0 z-10' : ''}`}
        >
          <nav className="flex space-x-4 mb-4" aria-label="Tabs">
            {['Organizations', 'Negotiation Scores', 'Trainee Analytics', 'Top Negotiations'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`
                  px-3 py-2 text-sm font-medium rounded-md
                  ${activeTab === tab
                    ? 'bg-[#071146] text-white'
                    : 'text-black-500 hover:bg-indigo-100'}
                  transition-colors duration-150 ease-in-out
                `}
              >
                {tab}
              </button>
            ))}
          </nav>

          <div>
            {activeTab === 'Organizations' && table3Data && <Table3 data={table3Data} reactVerbosity={reactVerbosity} />}
            {activeTab === 'Negotiation Scores' && negTableData && <NegNameTraineeScoreTable data={negTableData} reactVerbosity={reactVerbosity} />}
            {activeTab === 'Trainee Analytics' && table1Data && <Table1 data={table1Data} maxModuleCount={maxModuleCount} reactVerbosity={reactVerbosity} />}
            {activeTab === 'Top Negotiations' && topNegTableData && <Table2 data={topNegTableData} reactVerbosity={reactVerbosity} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDash;